import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Option, OptionTextLayout } from '../../models/select.model';
import { ControlValueAccessor } from '@angular/forms';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { provideValueAccessor } from 'studio-components/shared/value-accessor.utils';

@Component({
  selector: 'studio-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [provideValueAccessor(() => SelectComponent)],
})
export class SelectComponent implements ControlValueAccessor, OnChanges {
  @ViewChild(MatSelect, { static: true }) matSelect: MatSelect;
  @Input() public uniqueId: string;
  @Input() public options: Option<any>[] = [];
  @Input() public optionTextLayout: OptionTextLayout = OptionTextLayout.STACK; // default layout to multi-line option
  @Input() public disabled = false;
  @Input() public placeholder: string;
  @Input() public capitalizeOptionText = true;
  @Output() public selectionChange = new EventEmitter<MatSelectChange>();
  @Input() public get selected() {
    return this.selectedOption?.value;
  }
  public set selected(value: any) {
    this.updateSelectedOption(value);
  }
  public selectedOption: Option;

  /**
   * Gets the selected option based on the selected value in order to display only
   * the text/label property as the label for the Select Component.
   *
   * See TAM-241
   */
  private updateSelectedOption(value: any) {
    this.selectedOption = this.options.find((opt) => opt.value === value);
  }

  public selectOption(option, event) {
    if (this.selected && this.selected === option.value) {
      return;
    }

    if (!this.disabled && event.isUserInput) {
      this.selectionChange.emit(new MatSelectChange(this.matSelect, option));
      this.onChange(option.value);
      this.onTouched();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { options, selected } = changes;

    if (options && selected) {
      this.updateSelectedOption(selected.currentValue);
      if (this.onChange && this.selectedOption) {
        this.onChange(this.selectedOption.value);
        this.onTouched();
      }
    }
  }

  /**
   * REACTIVE FORMS INTEGRATION
   */
  public onChange = (_: any) => {};
  public onTouched = () => {};

  // writes a new value to the input.
  public writeValue(value: any): void {
    this.selected = value;
  }

  // function called when the control's value changes in the UI.
  public registerOnChange(fn): void {
    this.onChange = fn;
  }

  // function is called by forms API on initialization
  // to update the form model on blur/on touched.
  public registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  // set disabled state
  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
}
